// 有料 API を含む生成処理の実行履歴。
// プロジェクト配下へ保存し、Node 版はファイル、ブラウザ版は同じ VFS 経由で IndexedDB に永続化する。
import { randomUUID } from 'node:crypto';
import { projectPaths, requireProjectDir } from './story-project.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { normalizeCostSummary } from './generation-cost.mjs';

export const GENERATION_LOG_FORMAT_VERSION = '1.1';

function emptyStore() {
    return { formatVersion: GENERATION_LOG_FORMAT_VERSION, runs: [] };
}

function stringArray(value) {
    return Array.isArray(value) ? value.map((line) => String(line)) : [];
}

function normalizeCastLogs(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value)
        .filter(([castId]) => /^[A-Za-z0-9_-]+$/.test(castId))
        .map(([castId, lines]) => [castId, stringArray(lines)]));
}

function normalizeRecord(record = {}) {
    const now = new Date().toISOString();
    const id = typeof record.id === 'string' && /^[A-Za-z0-9:_-]{1,160}$/.test(record.id)
        ? record.id : randomUUID();
    const lines = stringArray(record.lines);
    const castLogs = normalizeCastLogs(record.castLogs);
    const costSummary = normalizeCostSummary(record.costSummary, lines, {
        kind: record.kind,
        castLogs,
    });
    return {
        id,
        kind: String(record.kind ?? 'unknown').slice(0, 100),
        mode: record.mode === 'batch' ? 'batch' : 'individual',
        ...(typeof record.batchRunId === 'string' ? { batchRunId: record.batchRunId.slice(0, 160) } : {}),
        ...(typeof record.batchStep === 'string' ? { batchStep: record.batchStep.slice(0, 80) } : {}),
        startedAt: typeof record.startedAt === 'string' ? record.startedAt : now,
        finishedAt: typeof record.finishedAt === 'string' ? record.finishedAt : now,
        status: ['running', 'done', 'failed', 'canceled'].includes(record.status) ? record.status : 'done',
        ...(typeof record.error === 'string' && record.error ? { error: record.error } : {}),
        ...(Array.isArray(record.castIds) ? { castIds: record.castIds.map(String) } : {}),
        lines,
        castLogs,
        ...(costSummary ? { costSummary } : {}),
    };
}

export function readGenerationLogs(projectId) {
    requireProjectDir(projectId);
    const stored = readJsonIfExists(projectPaths(projectId).generationLogs);
    if (!stored || !Array.isArray(stored.runs)) return emptyStore();
    return {
        formatVersion: GENERATION_LOG_FORMAT_VERSION,
        runs: stored.runs.map(normalizeRecord),
    };
}

/** 同じ id は更新、それ以外は追記。履歴は削除せず全実行分を保持する。 */
export function saveGenerationLog(projectId, record) {
    const store = readGenerationLogs(projectId);
    const normalized = normalizeRecord(record);
    const index = store.runs.findIndex((run) => run.id === normalized.id);
    if (index >= 0) store.runs[index] = normalized;
    else store.runs.push(normalized);
    writeJson(projectPaths(projectId).generationLogs, store);
    return normalized;
}
